export const TrcnExamInfo = {
  examName: 'TRCN Professional Qualifying Examination (PQE)',
  totalQuestions: 60,
  durationSeconds: 60 * 60, // 1 hour
  passMarkPercent: 50,
  passMarkRaw: 30, // 30/60
  notes: `Single-paper CBT: 60 MCQs, text-based only, no essays or diagrams. No negative marking. Candidates must score at least 50% (30/60) to pass. Exam is proctored; violations lead to disqualification.`
};

export const TrcnSubjects = [
  {
    subject: "English and Communication Skills",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Nature and importance of classroom communication; types of language problems among learners and improvement strategies.
        Elements of communication (sender, message, channel, receiver, feedback); barriers and effective strategies.
        Grammar: Parts of speech, verb tenses, subject-verb agreement, auxiliary/modals, questions/statements/commands; phrases, clauses, sentence types/patterns; direct/indirect speech; common errors and punctuation.
        Vocabulary development: Synonyms, antonyms, homophones, idioms, figures of speech; word formation.
        Listening skills: Types (informative, critical, appreciative); defects and strategies for effective listening.
        Speaking skills: Fluency drills, speech types (narrative, descriptive, argumentative, explanatory); oral communication in teaching.
        Reading skills: Purposes (skimming, scanning, critical reading); techniques for comprehension and inference.
        `,
    rubric: `Core skills section: high emphasis. Typical items are text-based MCQs testing comprehension, grammar, vocabulary, listening and basic writing strategies.
        Scoring: each MCQ = 1 mark; no negative marking; section weighted as a core language skill area.`,
  },
  {
    subject: "Basic Mathematics",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Binary numbers and conversions between bases; sets (definition, notation, Venn diagrams, operations).
        Basic operations on whole numbers, fractions, decimals, approximations; indices, surds, logarithms.
        Algebraic expressions: simple equations and inequalities; word problems.
        Formulae: Change of subject; substitution.
        Ratios, proportions, percentages; simple and compound interest; direct/inverse variations.
        Measurements: Units (money, length, mass, area, volume); conversions; areas/volumes of 2D/3D shapes.
        Geometry: Properties/classification of shapes; types of angles; lines.
        Statistics: Averages (mean, median, mode); range, simple probability.
        Applications in teaching, learning, and daily life.`,
    rubric: `Numeracy and problem-solving: medium-to-high emphasis. Questions test numerical manipulation, basic algebra, geometry and interpretation of simple data.
        Scoring: each MCQ = 1 mark; no negative marking; calculators not required but may be simulated in advanced items.`,
  },
  {
    subject: "History of Education",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concept and rationale of history of education; sources and methods of historical research.
        History of ancient education (Greek, Roman, African traditional, Islamic in Nigeria).
        Onset and evolution of Western education in Nigeria (missionary era, colonial policies).
        Nigerian education post-independence (1960 onwards): Policies, commissions, and reforms.
        Evolution of primary, secondary, and junior secondary education structures.
        Role of educational supervisory commissions; National Policy on Education and Nigerian Constitution.
        Historical challenges in Nigerian education; comparisons with global systems; lessons for current reforms.`,
    rubric: `Contextual and factual recall: low-to-medium emphasis. Questions test knowledge of historical events, policies and timelines.
        MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Philosophy of Education",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Philosophical thoughts influencing global and Nigerian education (ancient to modern).
        Schools of thought: Naturalism, Idealism, Realism, Pragmatism, Existentialism, Africanism; key philosophers (Plato, Rousseau, Dewey) and contributions.
        Evolution and tenets of developmentalism in Nigerian education philosophy.
        Goals, aims, and objectives of Nigerian education; concepts like rationality, justice, self-reliance, democracy, equality.
        Relevance to teacher education; appraising challenges with philosophical remedies.`,
    rubric: `Conceptual understanding and application: medium emphasis. Questions assess familiarity with schools of thought and their classroom implications.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Sociology of Education",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Sociological foundations: Influence of social factors on education and vice versa.
        Emerging issues: Gender, security, out-of-school children, family life, child protection, trafficking.
        Concepts: Nature/scope of sociology; socialization (types, agencies); group dynamics.
        Social stratification, culture, and education; education as a change agent.
        School as a formal organization; social change consequences.
        Contemporary issues: National integration, religious tolerance, population education.`,
    rubric: `Societal context and issues: medium emphasis. Questions evaluate understanding of social influences on education and policy implications.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Educational Psychology",
    code: "PQE 004",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Meaning, scope, and relevance to teaching; human reproduction, growth, and development principles.
        Stages/features: Physical, social, emotional, intellectual, moral (childhood, adolescence, adulthood).
        Theories of personality (Psycho-Analytic, Trait Theory); individual differences (heredity/environment).
        Learning concepts/theories: Behaviourist (Pavlov, Skinner), Cognitive (Piaget), Constructivist; applications, transfer, motivation.
        Memory, forgetting, rote/over-learning; reward/punishment effects.
        Barriers to learning; rural/urban influences; emotional development; communication skills.`,
    rubric: `Core pedagogical area: high emphasis. Questions test theories of learning, application to classroom practice and student differences.
         1 mark; no negative marking.`,
  },
  {
    subject: "Guidance and Counselling",
    code: "PQE 005",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concepts, distinctions, goals, history, and development in Nigeria; theories and ethics.
        Guidance services in schools; communication/study skills; organization/administration.
        Health counselling (HIV/AIDS, drug abuse); career/sex/marriage counselling.
        Tests in G&C; needs assessment, programme planning, consultation.
        Issues/trends: Interpersonal relationships, finance, ethical responsibilities.`,
    rubric: `Practical support and ethics: medium emphasis. Items test knowledge of guidance services, counselling approaches and school programme planning.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Teaching Profession",
    code: "PQE 006",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `TRCN Act provisions; mandates, programmes, successes, challenges.
        Concept of profession; historical development of teaching in Nigeria.
        Ethics, professional organizations; UNESCO/ILO teacher status.
        Role of TRCN in registration, licensing, CPD, induction; comparisons with other professions.`,
    rubric: `Policy and professional standards: low-to-medium emphasis. Items cover legal framework, professional ethics and institutional roles.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Teacher Education",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concepts, aims, objectives of teacher education (primary to tertiary levels).
        Trends/issues/strategies for improvement; roles of teachers in school/society.
        Teacher preparation and professional development.`,
    rubric: `Preparation and professional development: low-to-medium emphasis. Questions focus on training models, curriculum and teacher roles.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Classroom Management and School Organization",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concepts: Organization, leadership, arrangement, control, discipline; learner/school records.
        Effective classroom/school management; public relations factors.
        Relations: Staff-management, student-staff, parent-staff; school environment, programme, performance`,
    rubric: `Operational and leadership skills: medium emphasis. Items test routines, discipline models and organizational strategies.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Educational Technology and ICT",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concepts/history of educational technology; systems approach, multi-media integration.
        ICT role in education; computer basics (hardware/software, operations, booting).
        Applications: MS Word/Excel/PowerPoint; internet (browsing, searching, emailing); virtual libraries.
        Design/production/improvisation of media; community resources; evaluation of aids.`,
    rubric: `Digital literacy and tools: medium emphasis (growing). Questions cover practical ICT skills and applications to teaching.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Subject Methodology",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Principles/theories of teaching specific subjects; trends, improvisation, ODL/e-learning.
        Methods: Teacher/student-centred, group/individual, innovative (CAI); lesson planning.
        Objectives formulation; material selection/evaluation; curriculum implementation.
        Peer teaching; test construction; syllabus analysis.`,
    rubric: `Pedagogical techniques: medium emphasis. Items examine methods, planning and assessment alignment.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Child-Friendly Schools (CFS)",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concepts/principles/models based on UN Convention on Rights of the Child.
        Key areas: Child rights, effective/safe/healthy environments, gender sensitivity, participation.
        Nigerian policies; teacher preparation (constructivist/activity-based methods).
        Facilities/resources/assessment; technology mainstreaming`,
    rubric: `Child-rights and inclusion: low-to-medium emphasis. Questions focus on rights-based approaches and safe schooling practices.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Curriculum Studies",
    code: "PQE 012",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concepts/scope; types (broad fields, core, subject-based); development models.
        History of Nigerian primary/junior secondary curriculum; roles of NERDC/NCCE/NUC.
        Implementation, supervision, evaluation; organization/selection.`,
    rubric: `Design and implementation: medium emphasis. Items test curriculum models, agencies and evaluation strategies.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Measurement and Evaluation",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concepts/functions; types/uses of tests (teacher-made, standardized).
        Construction/administration/analysis; validity/reliability/usability.
        Continuous assessment principles/problems; norm/criterion-referenced.
        Non-cognitive methods (observation, checklists); examination ethics; computer-based testing.
        Statistics in testing (GPA/CGPA computation).`,
    rubric: `Assessment and psychometrics: medium-to-high emphasis. Questions test test-construction principles, analysis and ethical administration.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Educational Management",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Theories/principles; types of planning (manpower, social demand).
        Management of programmes/institutions/personnel/resources; quality assurance.
        Leadership styles; school supervision/discipline; financing/budgeting.
        Staff recruitment/welfare; MDGs/EFA/NEPAD in education; NGO roles.`,
    rubric: `Administration and leadership: medium emphasis. Items cover planning, resource management and quality assurance.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Special Education",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Historical development/National Policy in Nigeria; types of exceptionalities (causes, characteristics, identification).
        Interventions/strategies; attitudes/philosophy; inclusive education (implementation, collaboration).
        Curriculum/instructional needs for gifted/challenged students.`,
    rubric: `Inclusion and support strategies: low-to-medium emphasis. Items test identification, accommodations and inclusive practices.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Education of Special Target Groups/Adult Education",
    code: "PQE 016",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Rationale/principles for adults, women, nomads, migrants; non-formal/distance education.
        Adult learner characteristics; methods/materials; role in national development.
        Issues/problems; organizations; cross-cultural comparisons.`,
    rubric: `Non-formal and adult learning: low emphasis. Items focus on methods, learner characteristics and programme design.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Comparative Education",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Methods/scope; factors influencing systems; patterns of educational thought.
        Nigerian structure (1-6-3-3-4) vs. selected countries (Africa, Asia, Europe, America).
        Best practices; EFA/MDGs; post-2015 agenda.`,
    rubric: `Cross-system comparison: low emphasis. Items test ability to compare systems and identify transferable lessons.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Educational Statistics",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Data collection/presentation; measures of central tendency/dispersion.
        Inferential statistics: Estimation, hypothesis testing, correlation/regression.
        Analysis of variance/covariance; ICT tools for analysis.`,
    rubric: `Quantitative analysis: medium emphasis. Items cover descriptive and basic inferential statistics relevant to school data.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Educational Research Methods",
    code: "PQE 019",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Nature/types (historical, descriptive, experimental, qualitative/quantitative).
        Process: Problem identification, hypothesis, literature review, design, data collection (tools, validity/reliability).
        Analysis/interpretation/reporting; proposal writing; citations.`,
    rubric: `Research literacy: medium-to-high emphasis. Questions evaluate understanding of research design, instruments and reporting.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Use of Library",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Objectives/functions/types; materials/structure (bibliography, cataloguing, classification).
        Management/automation; virtual/e-libraries; online searching.`,
    rubric: `Information literacy: low emphasis. Items test knowledge of library systems, cataloguing and searching strategies.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  },
  {
    subject: "Micro Teaching Theory",
    timeAlloted: TrcnExamInfo.durationSeconds,
    description: `Concepts/process/phases; relevance to teacher education.
        Skills/techniques: Set induction, stimulus variation, reinforcement, questioning, closure.
        Roles in sessions; merits/demerits; peer/supervised micro-teaching.`,
    rubric: `Practical teaching skills: low emphasis. Items examine micro-teaching stages and skill-focused techniques.
        Scoring: each MCQ = 1 mark; no negative marking.`,
  }
];
